import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CalendarDays, UserRound, Info, Leaf, Target, Eye, Compass } from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { PageBanner, Pagination, SectionHeading, Txt, Logo } from "@/components/site/Brand";
import { NewsCard } from "@/components/site/Cards";
import { CtaBand } from "@/components/site/Layout";
import NotFound from "./NotFound";

const CATS = ["Announcements", "Insights", "Updates"];

export function News() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  useEffect(() => { document.title = "News and insights – IRID"; }, []);
  useEffect(() => {
    let live = true;
    api.get("/news", { params: Object.fromEntries(params) }).then((r) => live && setData(r.data)).catch(() => live && setData({ items: [], total: 0, page: 1, pages: 1 }));
    return () => { live = false; };
  }, [params]);
  const cat = params.get("category") || "";
  return (
    <>
      <PageBanner title="News and insights" eyebrow="News" intro="Articles, announcements and updates from IRID." crumbs={[{ to: "/", label: "Home" }, { label: "News" }]} testid="news-banner" />
      <section className="bg-irid-mist py-14 md:py-20">
        <div className="container-irid">
          <div className="mb-10 flex flex-wrap gap-2" role="group" aria-label="Filter by category">
            {["", ...CATS].map((c) => (
              <button key={c || "all"} type="button" aria-pressed={cat === c} onClick={() => setParams(c ? { category: c } : {})} data-testid={`news-filter-${c || "all"}`}
                className={`min-h-[44px] rounded-full border px-5 text-sm font-semibold ${cat === c ? "border-irid-navy bg-irid-navy text-white" : "border-irid-cloud bg-white text-irid-blue hover:border-irid-blue"}`}>
                {c || "All"}
              </button>
            ))}
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="news-results">{data?.items.map((n, i) => <NewsCard key={n.slug} n={n} index={i} />)}</div>
          {data && data.items.length === 0 && <p className="text-irid-slate" data-testid="news-empty-state">No articles in this category yet.</p>}
          {data && <Pagination page={data.page} pages={data.pages} onPage={(p) => { const n = new URLSearchParams(params); n.set("page", p); setParams(n); }} testid="news-pagination" />}
        </div>
      </section>
    </>
  );
}

export function NewsDetail() {
  const { slug } = useParams();
  const [n, setN] = useState(undefined);
  useEffect(() => {
    let live = true;
    setN(undefined);
    api.get(`/news/${slug}`).then((r) => live && setN(r.data)).catch(() => live && setN(null));
    return () => { live = false; };
  }, [slug]);
  if (n === null) return <NotFound />;
  if (!n) return <div className="min-h-[60vh]" aria-busy="true" />;
  return (
    <>
      <PageBanner title={<Txt>{n.title}</Txt>} eyebrow={n.category} crumbs={[{ to: "/", label: "Home" }, { to: "/news", label: "News" }, { label: "Article" }]} testid="article-banner">
        <p className="mt-6 flex flex-wrap gap-6 text-white/90">
          <span className="inline-flex items-center gap-2"><CalendarDays aria-hidden="true" className="h-4 w-4" /><time dateTime={n.date}>{formatDate(n.date)}</time></span>
          <span className="inline-flex items-center gap-2"><UserRound aria-hidden="true" className="h-4 w-4" /><Txt>{n.author}</Txt></span>
        </p>
      </PageBanner>
      <article className="bg-white py-16">
        <div className="container-irid max-w-3xl space-y-6 text-lg" data-testid="article-body">
          <Txt as="p" className="text-xl font-semibold text-irid-navy">{n.excerpt}</Txt>
          {n.content.map((c, i) => <Txt key={i} as="p">{c}</Txt>)}
          <Link to="/news" className="btn-secondary mt-6" data-testid="article-back">Back to all news</Link>
        </div>
      </article>
      {n.related.length > 0 && (
        <section className="bg-irid-mist py-16">
          <div className="container-irid">
            <SectionHeading title="More news" />
            <div className="grid gap-6 md:grid-cols-3">{n.related.map((x, i) => <NewsCard key={x.slug} n={x} index={i} />)}</div>
          </div>
        </section>
      )}
    </>
  );
}

export function Leadership() {
  const [profiles, setProfiles] = useState([]);
  useEffect(() => { document.title = "Leadership – IRID"; api.get("/profiles").then((r) => setProfiles(r.data)); }, []);
  const groups = ["Board", "Executive"];
  return (
    <>
      <PageBanner title="Leadership and governance" eyebrow="About IRID" intro="The board and executive team responsible for IRID's direction and governance." crumbs={[{ to: "/", label: "Home" }, { to: "/about", label: "About" }, { label: "Leadership" }]} testid="leadership-banner" />
      <section className="bg-white py-16 md:py-20">
        <div className="container-irid">
          <div className="mb-12 flex gap-3 rounded-xl border border-irid-blue/20 bg-irid-blue/5 p-5 text-[15px]" data-testid="leadership-approval-notice">
            <Info aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-irid-blue" />
            <p>Profile names, titles, biographies, credentials and photographs are published only after IRID approves them (TEAM-002). The cards below are placeholders.</p>
          </div>
          {groups.map((g) => (
            <div key={g} className="mb-16">
              <SectionHeading title={g === "Board" ? "Board of Directors" : "Executive leadership"} />
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {profiles.filter((p) => p.group === g).map((p) => (
                  <article key={p.slug} className="card-irid card-top-growth p-7 pt-8" data-testid={`profile-card-${p.slug}`}>
                    <div className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-dashed border-irid-cloud bg-irid-mist" role="img" aria-label="Photograph awaiting IRID approval">
                      <UserRound aria-hidden="true" className="h-10 w-10 text-irid-slate" />
                    </div>
                    <h3 className="mt-6 text-lg"><Txt>{p.name}</Txt></h3>
                    <p className="mt-2 text-sm font-semibold text-irid-blue"><Txt>{p.official_title}</Txt></p>
                    <p className="mt-4 text-[15px]"><Txt>{p.biography}</Txt></p>
                    <p className="mt-3 text-sm text-irid-slate"><Txt>{p.credentials}</Txt></p>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

export function About() {
  const { settings: s } = useSite();
  useEffect(() => { document.title = "About – IRID"; }, []);
  const blocks = [[Eye, "Vision", s?.vision], [Compass, "Mission", s?.mission]];
  return (
    <>
      <PageBanner title="About IRID" eyebrow="Research • Innovation • Development" intro="A Liberian institution generating credible evidence, strengthening institutions, and supporting enterprise growth." crumbs={[{ to: "/", label: "Home" }, { label: "About" }]} testid="about-banner" />
      <section className="bg-white py-20">
        <div className="container-irid grid items-start gap-14 lg:grid-cols-12">
          <div className="space-y-12 lg:col-span-7">
            <div data-testid="about-institutional">
              <div className="flex items-center gap-3"><Target aria-hidden="true" className="h-6 w-6 text-irid-blue" /><h2 className="text-2xl md:text-3xl">About IRID</h2></div>
              <div className="growth-bar mt-3" aria-hidden="true" />
              <p className="mt-5 text-lg">{s?.about_intro}</p>
              {(s?.about_more || []).map((t, i) => <p key={i} className="mt-4 text-lg">{t}</p>)}
            </div>
            <div data-testid="about-mandate">
              <div className="flex items-center gap-3"><Target aria-hidden="true" className="h-6 w-6 text-irid-green" /><h2 className="text-2xl md:text-3xl">Mandate</h2></div>
              <div className="growth-bar mt-3" aria-hidden="true" />
              <p className="mt-5 text-lg">{s?.mandate_intro} Specifically, IRID is mandated to:</p>
              <ol className="mt-6 space-y-3">
                {(s?.mandate || []).map((m, i) => (
                  <li key={i} className="flex gap-4 rounded-lg border border-irid-cloud p-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-irid-navy font-heading text-sm font-bold text-white" aria-hidden="true">{i + 1}</span><span>{m}</span>
                  </li>
                ))}
              </ol>
            </div>
            {blocks.map(([Icon, t, text]) => (
              <div key={t} data-testid={`about-${t.split(" ")[0].toLowerCase()}`}>
                <div className="flex items-center gap-3"><Icon aria-hidden="true" className="h-6 w-6 text-irid-blue" /><h2 className="text-2xl md:text-3xl">{t}</h2></div>
                <div className="growth-bar mt-3" aria-hidden="true" />
                <Txt as="p" className="mt-5 text-lg">{text}</Txt>
              </div>
            ))}
            <div data-testid="about-values">
              <h2 className="text-2xl md:text-3xl">Our values</h2>
              <div className="growth-bar mt-3" aria-hidden="true" />
              <ul className="mt-6 grid gap-4 sm:grid-cols-2">
                {(s?.values || []).map((v) => (
                  <li key={v.name} className="flex items-start gap-3 rounded-lg bg-irid-mist p-5" data-testid={`about-value-${v.name.split(" ")[0].toLowerCase()}`}>
                    <Leaf aria-hidden="true" className="mt-1 h-4 w-4 shrink-0 text-irid-green" />
                    <span><strong className="block font-heading text-irid-navy">{v.name}</strong><span className="mt-1 block text-[15px]">{v.description}</span></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <aside className="flex flex-col items-center rounded-2xl bg-irid-mist p-10 text-center lg:col-span-5">
            <div className="rounded-2xl bg-white p-6"><Logo size={212} testid="about-logo" /></div>
            <p className="mt-6 font-heading text-sm font-semibold uppercase tracking-[0.08em] text-irid-navy">The Institute for Research, Innovation and Development</p>
            <Link to="/leadership" className="btn-primary mt-8" data-testid="about-leadership-link">Meet our leadership</Link>
          </aside>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
